import { IModalRefProps } from "@repo/core/components/layout/Modal/types";
import { AlertModalVariant } from "@repo/core/components/layout/ModalTemplate/AlertModal/types";
import { IModalOption } from "@repo/core/components/layout/ModalTemplate/types";
import { useRouteParams } from "@repo/core/hooks/useRouteParams";
import { ROUTES } from "@repo/core/routes";
import { useProposalFlowNavigation } from "../../utils/proposalFlowNavigation";
import { useCreditProposal } from "../hooks/useCreditProposal";
import { useMemo, useRef, useState } from "react";
import {
  formatDateFromISO,
  resolveAdmissionDateIso,
  resolveIncomeAmount
} from "../hooks/useCreditProposal/mappers";
import { formatCPF, formatCellPhone } from "@repo/core/utils/mask.utils";
import {
  ConsultProposalResponseDTO,
  TIPO_PESSOA
} from "@repo/core/services/entities/Proposals";
import {
  getCreditProposalPostSaveNavigation,
  isCreditAnalysisProcessingStatus
} from "@repo/core/screens/Proposals/rules/creditTableCompletionFlow";
import { useProposalStatusPolling } from "@repo/core/hooks/useProposalStatusPolling";
import { useQueryClient } from "@tanstack/react-query";
import { useSelectedProposalServicePoint } from "@repo/core/hooks/useProposalServicePoints";

export const ReviewProposalController = () => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [analysisProposalId, setAnalysisProposalId] = useState<string | null>(
    null
  );
  const [isWaitingCreditAnalysis, setIsWaitingCreditAnalysis] = useState(false);
  const hasNavigatedAfterAnalysisRef = useRef(false);

  const { replace, back, openReviewEdit } = useProposalFlowNavigation();

  const {
    proposalData,
    postSaveAction,
    resetCreditProposalStore,
    saveCreditProposal
  } = useCreditProposal();

  const { selectedServicePoint } =
    useSelectedProposalServicePoint(proposalData);

  // Map proposal data to display format
  const securityData = useMemo(() => {
    if (!proposalData) {
      return {
        cpf: "",
        fullName: "",
        cellPhone: ""
      };
    }

    // Get CPF/CNPJ - check both strCpf and strCliente
    const cpfOrCnpj = proposalData.strCpf || proposalData.strCliente || "";

    // Get phone fields - use strCelular/strCelularDDD if available, otherwise use strFoneEnvioToken/strDDDEnvioToken
    const strEndFone1 =
      proposalData.strCelular || proposalData.strFoneEnvioToken || "";
    const strEndDdd1 =
      proposalData.strCelularDDD || proposalData.strDDDEnvioToken || "";

    return {
      cpf: formatCPF(cpfOrCnpj),
      fullName: proposalData.strNome || "",
      cellPhone: formatCellPhone(strEndDdd1, strEndFone1)
    };
  }, [proposalData]);

  const personalData = useMemo(() => {
    if (!proposalData) {
      return {
        birthday: "",
        gender: null,
        martialStatus: "",
        mothersName: "",
        fathersName: "",
        personType: TIPO_PESSOA.FISICA,
        documentIdentification: ""
      };
    }
    return {
      birthday: formatDateFromISO(proposalData.datDataNascimento),
      gender: proposalData.strSexo || null,
      personType:
        (proposalData.strTipoPessoa as TIPO_PESSOA) || TIPO_PESSOA.FISICA,
      martialStatus: proposalData.strEstadoCivil || "",
      mothersName: proposalData.strNomeMae || "",
      fathersName: proposalData.strNomePai || "",
      documentIdentification: proposalData.strNroDocIdent || ""
    };
  }, [proposalData]);

  const addressData = useMemo(() => {
    if (!proposalData) {
      return {
        residenceType: "",
        zipCode: "",
        addressLine1: "",
        addressLine2: "",
        number: "",
        neighborhood: "",
        city: "",
        state: ""
      };
    }
    return {
      residenceType:
        proposalData.strEndTipoResidencia ||
        proposalData.strResTipoResidencia ||
        "",
      zipCode: proposalData.strResCep || proposalData.strCep || "",
      addressLine1:
        proposalData.strResEndereco || proposalData.strEndereco || "",
      addressLine2:
        proposalData.strResComplemento || proposalData.strComplemento || "",
      number: proposalData.strResNumero || proposalData.strNumero || "",
      neighborhood: proposalData.strResBairro || proposalData.strBairro || "",
      city: proposalData.strResCidade || proposalData.strCidade || "",
      state: proposalData.strResUF || proposalData.strUf || ""
    };
  }, [proposalData]);

  const professionData = useMemo(() => {
    if (!proposalData) {
      return {
        professionalCategory: "",
        profession: "",
        admissionDate: "",
        income: "",
        residenceType: "",
        professionalClassDescription: "",
        professionName: ""
      };
    }

    // Get descriptions from store (saved when form was filled)
    // Uses strClasseProfissionalDescricao and strProfissaoDescricao from store if available
    const professionalCategoryDescription =
      (
        proposalData as Partial<
          ConsultProposalResponseDTO & {
            strClasseProfissionalDescricao?: string;
          }
        >
      ).strClasseProfissionalDescricao || "";
    const professionDescription =
      (
        proposalData as Partial<
          ConsultProposalResponseDTO & {
            strProfissaoDescricao?: string;
          }
        >
      ).strProfissaoDescricao || "";

    const incomeValue = resolveIncomeAmount(proposalData);
    const income = incomeValue && incomeValue > 0 ? incomeValue.toString() : "";

    return {
      professionalCategory: professionalCategoryDescription,
      profession: professionDescription,
      admissionDate: formatDateFromISO(resolveAdmissionDateIso(proposalData)),
      income,
      residenceType: proposalData.strEndTipoResidencia || "",
      professionalClassDescription: professionalCategoryDescription,
      professionName: professionDescription
    };
  }, [proposalData]);

  // Get product from proposalData
  const selectedProduct = useMemo(() => {
    if (!proposalData?.strProdutoDescricao && !proposalData?.strProduto) {
      return null;
    }
    return {
      name: proposalData.strProdutoDescricao || "",
      number: proposalData.strProduto || ""
    };
  }, [proposalData]);

  const alertModalRef = useRef<IModalRefProps>(null);
  const params = useRouteParams();

  const isAccordionsStartClosed = params?.isAccordionsStartClosed;
  const isOnlyView = params?.isOnlyView;

  const isAccordionsStartOpened = useMemo(() => {
    if (isAccordionsStartClosed) {
      return false;
    }

    return true;
  }, [isAccordionsStartClosed]);

  const { status: currentAnalysisStatus, error: analysisPollingError } =
    useProposalStatusPolling({
      proposalId: analysisProposalId,
      enabled: isWaitingCreditAnalysis && !!analysisProposalId,
      pollingInterval: 3000,
      onStatusChange: (newStatus) => {
        if (
          isCreditAnalysisProcessingStatus(newStatus) ||
          hasNavigatedAfterAnalysisRef.current ||
          !analysisProposalId
        ) {
          return;
        }

        hasNavigatedAfterAnalysisRef.current = true;
        setIsWaitingCreditAnalysis(false);
        resetCreditProposalStore();
        replace(ROUTES.proposalsViewInfo, {
          proposalId: analysisProposalId
        });
      }
    });

  const handleGoBack = () => {
    back();
  };

  const handleAbandon = () => {
    if (proposalData?.lngProposta) {
      resetCreditProposalStore();
      replace(ROUTES.proposalsViewInfo, {
        proposalId: proposalData.lngProposta
      });
    } else {
      back();
    }
  };

  const handleClose = () => {
    resetCreditProposalStore();
    replace(ROUTES.home);
  };

  const handleSubmit = async () => {
    if (isSaving || isWaitingCreditAnalysis) return;

    try {
      setIsSaving(true);
      setIsLoading(true);
      setErrorMessage(null);
      alertModalRef.current?.open();

      const savedProposal = await saveCreditProposal();
      const savedProposalData = savedProposal as
        | Partial<ConsultProposalResponseDTO>
        | undefined;
      const savedProposalId =
        savedProposalData?.lngProposta || proposalData?.lngProposta;

      setIsLoading(false);
      setIsSaving(false);
      setErrorMessage(null);

      const postSaveNavigation = getCreditProposalPostSaveNavigation(
        postSaveAction,
        savedProposalId
      );

      if (postSaveNavigation.route === ROUTES.proposalsViewInfo) {
        if (!savedProposalId) {
          throw new Error(
            "No proposal ID received from alter proposal endpoint"
          );
        }

        queryClient.removeQueries({
          queryKey: ["proposalSituation", String(savedProposalId), false],
          exact: true
        });
        hasNavigatedAfterAnalysisRef.current = false;
        setAnalysisProposalId(String(savedProposalId));
        setIsWaitingCreditAnalysis(true);
        return;
      }

      resetCreditProposalStore();
      replace(postSaveNavigation.route, postSaveNavigation.params);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setIsLoading(false);
      setIsSaving(false);

      // Extract error message from API response
      let message = "Não foi possível salvar a proposta. Tente novamente.";
      if (error?.response?.data?.strMensagem) {
        message = error.response.data.strMensagem;
      } else if (error?.message) {
        message = error.message;
      }

      setErrorMessage(message);
      // Keep modal open to show error message
    }
  };

  const handleEditPersonalData = () => {
    openReviewEdit(ROUTES.creditProposalPersonalData);
  };

  const handleEditAddressData = () => {
    openReviewEdit(ROUTES.creditProposalAddress);
  };

  const handleEditProfessionData = () => {
    openReviewEdit(ROUTES.creditProposalProfessionData);
  };

  const handleEditSelectProduct = () => {
    openReviewEdit(ROUTES.creditProposalSelectProduct);
  };

  const handleEditFinancialServices = () => {
    openReviewEdit(ROUTES.creditProposalSelectFinancialService);
  };

  const handleEditSelectServicePoint = () => {
    openReviewEdit(ROUTES.creditProposalSelectServicePoint);
  };

  const alertOptions: IModalOption[] = [
    {
      label: "Visualizar Proposta",
      onPress: () => {
        alertModalRef.current?.close();
        resetCreditProposalStore();
        replace(ROUTES.proposalsViewInfo, {
          proposalId: proposalData?.lngProposta
        });
      }
    },
    {
      label: "Ver resultado da análise",
      onPress: () => alertModalRef.current?.close()
    }
  ];

  const getAlertModalProps = () => {
    if (isLoading || isSaving) {
      return {
        title: "Por favor, aguarde...",
        text: "Salvando a proposta...",
        variant: AlertModalVariant.success,
        options: [],
        isLoading: true
      };
    }

    if (isWaitingCreditAnalysis) {
      if (analysisPollingError) {
        return {
          title: "Erro ao consultar análise",
          variant: AlertModalVariant.error,
          text: "Não foi possível consultar o status da análise de crédito. Tente novamente.",
          options: [
            {
              label: "Fechar",
              onPress: () => {
                alertModalRef.current?.close();
                setIsWaitingCreditAnalysis(false);
                setAnalysisProposalId(null);
              }
            }
          ],
          isLoading: false
        };
      }

      return {
        title: "Análise de crédito em andamento",
        text: currentAnalysisStatus
          ? `A proposta está em processamento (${currentAnalysisStatus}). Aguarde a conclusão da análise.`
          : "A proposta está em processamento. Aguarde a conclusão da análise.",
        variant: AlertModalVariant.success,
        options: [],
        isLoading: true
      };
    }

    if (errorMessage) {
      return {
        title: "Erro ao salvar proposta",
        variant: AlertModalVariant.error,
        text: errorMessage,
        options: [
          {
            label: "Fechar",
            onPress: () => {
              alertModalRef.current?.close();
              setErrorMessage(null);
            }
          }
        ],
        isLoading: false
      };
    }

    return {
      title: "Proposta salva",
      variant: AlertModalVariant.success,
      text: "A proposta de crédito foi salva com sucesso.",
      options: alertOptions,
      isLoading: false
    };
  };

  return {
    handleGoBack,
    handleSubmit,
    handleEditAddressData,
    handleEditPersonalData,
    isAccordionsStartOpened,
    handleEditProfessionData,
    handleEditSelectServicePoint,
    handleEditSelectProduct,
    handleClose,
    handleAbandon,
    securityData,
    personalData,
    addressData,
    handleEditFinancialServices,
    professionData,
    selectedProduct,
    selectedServicePoint,
    isOnlyView,
    isSaving,
    isWaitingCreditAnalysis,
    alertModalRef,
    getAlertModalProps
  };
};
